import React from "react";

import { AssignmentWrapper } from "common/entities/assignmentWrapper/AssignmentWrapper";
import { CourseWrapper } from "common/entities/courseWrapper/CourseWrapper";
import { FlexGrow, FlexJustify } from "common/fragments/Flex";
import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { CardVariant, CardView } from "common/views/card/Card.view";
import { DropdownSelectVariant } from "common/views/dropdownSelect/DropdownSelect.view";
import { OptionDropdownSelect } from "common/views/dropdownSelect/OptionDropdownSelect";
import HeadingView, { HeadingVariant } from "common/views/heading/Heading.view";
import { Query } from "core/api/typedQuery";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { isEmpty } from "core/util/ArrayUtils";
import { validateList } from "core/util/ParamUtil";
import StringUtil from "core/util/StringUtil";
import { iDef, iDefL } from "core/util/TypeUtils";
import labelProvider from './Assignments.labels';
import AssignmentsConfig from "./AssignmentsConfig";
import Assignment from "./entities/Assignment";

const { Labels } = labelProvider;
interface IAssignmentsParams {
	sort?: string;
}

export const PARAM_ASSIGNMENTS_SORT = "assignments.sort";
export const PARAM_ASSIGNMENTS_PAGE_SIZE = "assignments.pageSize";

const sortOptions = [
	{value: "-endDate", label: <Labels.DueDate />},
	{value: "name", label: <Labels.AssignmentName />}
];

const AssignmentsQuery = Query.queryMany<Assignment, IAssignmentsParams>(Assignment);

@Panel({id: "assignments", configClass: AssignmentsConfig, pageParams: true, labelProvider})
export class AssignmentsPanel extends React.PureComponent<IPanelProps<AssignmentsConfig>> {


	public render() {
		const { config, pageParams } = this.props;

		const defaultPageSize: number = iDef(config.pageSize) ? config.pageSize : 4;
		const currentPageSize: number = StringUtil.toInteger(pageParams[PARAM_ASSIGNMENTS_PAGE_SIZE], defaultPageSize);

		const params = config.enableRefine ? {
			sort: validateList(['-endDate', 'name'], pageParams[PARAM_ASSIGNMENTS_SORT], null)
		} : {};

		return [
			config.enableRefine && (
				<BlockView variant={BlockVariant.REFINE} viewKey="refine">
					<OptionDropdownSelect
						options={sortOptions}
						onChange={this.onChangeParam(PARAM_ASSIGNMENTS_SORT)}
						value={pageParams[PARAM_ASSIGNMENTS_SORT]}
						variant={DropdownSelectVariant.MINIMAL}
						viewKey="refine.sort"
					>
						<Labels.Sort />
					</OptionDropdownSelect>
				</BlockView>
			),
			<AssignmentsQuery key="query" params={params}>
				{
					({data, loading}) => {
						if (loading) {
							return <Loading key="loading" />;
						}

						if (isEmpty(data)) {
							return (
								<NonIdealState
									key="empty"
									title={<Labels.AssignmentEmpty />}
									description={<Labels.DescriptionEmpty />}
								/>
							);
						}

						return (
							<div key="data">
								{data.map(this.renderAssignment)}
								{
									config.enableLoadMore && data.length >= currentPageSize && (
										<BlockView variant={BlockVariant.PAGING} viewKey="paging">
											<ButtonView
												onClick={() => this.onChangeParam(PARAM_ASSIGNMENTS_PAGE_SIZE)(currentPageSize + defaultPageSize)}
												variant={ButtonVariant.PRIMARY}
												viewKey="load-more"
											>
												<Labels.LoadMore />
											</ButtonView>
										</BlockView>
									)
								}
							</div>
						);
					}
				}
			</AssignmentsQuery>
		];
	}

	private renderAssignment = (assignment: Assignment) => {
		const aw = AssignmentWrapper.forAssignment(assignment);

		if (!aw) {
			return null;
		}

		const cw = aw.getCourse();

		return (
			<CardView variant={CardVariant.SURFACE} viewKey="card">
				<FlexJustify>
					<FlexGrow>
						{cw && <HeadingView children={cw.getTitle()} variant={HeadingVariant.H4} viewKey="course-title" />}
						<HeadingView children={aw.getName()} viewKey="assignment-title" />
					</FlexGrow>
					<div>
						{this.renderActions(aw, cw)}
					</div>
				</FlexJustify>
			</CardView>
		);
	}

	private renderActions = (w: AssignmentWrapper, cw: CourseWrapper) => {

		const daysTilDue = w.getDaysUntilDue();

		const showWarning = (!w.getLatestSubmission() && daysTilDue < 7);

		return [
			showWarning && <Labels.DueDateWarning args={{daysTilDue: `${daysTilDue}`}}/>,
			w.getLatestSubmission() ? (
				<ButtonView key="review" variant={ButtonVariant.MINIMAL} viewKey="review" children={w.getLatestScoreText()} />
			) : (
				<ButtonView key="submit" onClick={() => cw && cw.launchCourse()} variant={ButtonVariant.PRIMARY} viewKey="submit">
					<Labels.Submit />
				</ButtonView>
			)
		];
	}

	private onChangeParam = (name: string) => (value: any) => {
		if (iDefL(value)) {
			this.props.actions.pushPageParam(name, value);
		} else {
			this.props.actions.removePageParam(name);
		}
	}
}





// WEBPACK FOOTER //
// ./src/panels/assignments/Assignments.panel.tsx